import * as firestoreRedux from '@dreamworld/firestore-redux';
import get from 'lodash-es/get';
import find from 'lodash-es/find';
import isEmpty from 'lodash-es/isEmpty';
import values from 'lodash-es/values';
import forEach from 'lodash-es/forEach';
import isEqual from 'lodash-es/isEqual.js';
import * as cardSelectors from '../card/selectors.js';
import * as columnSelectors from '../columns/selectors.js';
import * as authSelectors from '../auth/selectors.js';
import * as board from '../board';
import * as views from '../views';
import * as attachmentsSelectors from '../attachments/selectors.js';
import * as taskSelectors from '../task/selectors.js';
import { createSelector } from 'reselect';
import { getIdWoPrefix } from '../../utils';
import * as chatSelectors from '../chat/selectors.js';

const _cardUnreadDoc = (state, cardId) =>
  firestoreRedux.selectors.doc(
    state,
    'card-unread-user-details',
    `cuu_${getIdWoPrefix({ id: cardId, prefix: 'crd_' })}_${getIdWoPrefix({ id: authSelectors.currentUserId(state), prefix: 'usr_' })}`
  );

/**
 * Where a column sits along the board, left to right, the way `board.selectors.columns` lays it out:
 * ordinary columns by their own `order`, then DONE, then TRASH. DONE and TRASH are created without an
 * `order`, so they rank after every ordinary column instead.
 *
 * @param {Object} column Column doc. `{}` when it isn't loaded or no longer exists.
 * @returns {Number} `undefined` when the column has no position to compare.
 */
const _columnPosition = (column) => {
  const type = get(column, 'type');
  if (type === 'DONE') {
    return Number.MAX_SAFE_INTEGER - 1;
  }
  if (type === 'TRASH') {
    return Number.MAX_SAFE_INTEGER;
  }
  const order = get(column, 'order');
  return order === null ? undefined : order;
};

/**
 * @param {*}
 *  @property {Object} state Redux state
 *  @property {String} cardId Card Id.
 * @returns {Object} card unread details. e.g. { created: true, title: true, description: true, dueDate: true, tasks: true, chats: true, attachments: true }
 */
export const cardUnreadChanges = createSelector(
  ({ state, cardId }) => board.selectors.isBoardActive(state, cardSelectors.cardBoardId(state, cardId)),
  ({ state, cardId }) => _cardUnreadDoc(state, cardId),
  (isBoardActive, doc) => {
    if (!isBoardActive) {
      return {};
    }

    if(!doc || !doc.anyUnread) {
      return {};
    }

    const cardUnreads = get(doc, 'unread', {});
    if (cardUnreads.created) {
      return { created: true };
    }

    return cardUnreads;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 3000,
    },
  }
);

/**
 * Whether the board card has a mark for this unread key. The server can leave a key true that the card cannot
 * show: a chat or attachment count back to 0, an emptied description, a due date on a done or trashed card, a key
 * the card doesn't know. The orange arrows follow `isCardUnread`, so such a key led them to a card with nothing on it.
 */
const _isUnreadKeyShown = (key, card, summary) => {
  switch (key) {
    case 'created':
    case 'title':
    case 'columnId':
    case 'order':
      return true;
    case 'description':
      return !!(get(card, 'descriptionHtml') ?? get(card, 'description'));
    case 'dueDate':
      return !get(card, 'doneAt') && !get(card, 'trashedAt');
    // A summary not loaded yet can't overrule the server.
    case 'chats':
      return !summary || !!summary.chats;
    case 'attachments':
      return !summary || !!summary.attachments;
    case 'tasks':
      return !summary || !!(get(summary, 'tasks.open') || get(summary, 'tasks.closed') || summary.openTasks || summary.closedTasks);
    default:
      return false;
  }
};

const _hasShownUnread = (unread, card, summary) => {
  if (get(unread, 'created')) {
    return true;
  }
  return Object.keys(unread || {}).some((key) => unread[key] && _isUnreadKeyShown(key, card, summary));
};

/**
 * @returns { Boolean } card has an unread change that the board card shows.
 */
export const isCardUnread = createSelector(
  ({ state, cardId }) => cardUnreadChanges({state, cardId}),
  ({ state, cardId }) => cardSelectors.attrs(state, cardId),
  ({ state, cardId }) => firestoreRedux.selectors.doc(state, 'cards-summary', `cs_${getIdWoPrefix({ id: cardId, prefix: 'crd_' })}`),
  (_unreadUpdates, card, summary) => _hasShownUnread(_unreadUpdates, card, summary),
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 3000,
    },
  }
);

/**
 * Direction of the move the current user hasn't read yet, as a glyph the card chip can draw.
 *
 * The card remembers where it came from, so nothing about the direction is stored on the unread
 * document: `prevColumnId` is compared against the card's live column, and the two columns' own
 * `order` keys decide which way that was, with DONE and TRASH ranked after every other column.
 * Comparing the stored keys rather than the columns' positions is what keeps a deleted or reordered
 * third column out of the answer.
 *
 * @param {*}
 *  @property {Object} state Redux state
 *  @property {String} cardId Card Id.
 * @returns {String} 'UP', 'DOWN', 'LEFT' or 'RIGHT'. `undefined` when the move has no direction to
 *                   draw: a card created or moved in from another board, or a previous column that
 *                   is gone.
 */
export const cardMoveDirection = createSelector(
  ({ state, cardId }) => cardUnreadChanges({ state, cardId }),
  ({ state, cardId }) => cardSelectors.attrs(state, cardId),
  // Read straight off the document rather than through `cardUnreadChanges`. That returns a map
  // whose values are scanned for truthiness to decide whether a card is unread at all, and a
  // frozen position living in it would keep every card unread after it had been read.
  ({ state, cardId }) => get(_cardUnreadDoc(state, cardId), 'prevColumnOrder'),
  ({ state, cardId }) => columnSelectors.column(state, get(cardSelectors.attrs(state, cardId), 'columnId')),
  // Falls back to the card's own previous column while the unread document has none. That keeps
  // left and right working against a server that hasn't shipped the per-viewer value yet, at the
  // cost of describing the last hop rather than everything this viewer missed.
  ({ state, cardId }) => columnSelectors.column(state,
    get(_cardUnreadDoc(state, cardId), 'prevColumnId')
      || get(cardSelectors.attrs(state, cardId), 'prevColumnId')),
  (unread, card, prevColumnOrder, toColumn, fromColumn) => {
    if (!unread) {
      return undefined;
    }

    // A column change is the larger fact, so it wins when both are unread for this viewer.
    if (unread.columnId) {
      const from = _columnPosition(fromColumn);
      const to = _columnPosition(toColumn);
      if (from === undefined || to === undefined || from === to) {
        return undefined;
      }
      return to > from ? 'RIGHT' : 'LEFT';
    }

    if (!unread.order) {
      return undefined;
    }

    const from = prevColumnOrder;
    const to = get(card, 'columnOrder');
    if (from === undefined || from === null || to === undefined || to === null || from === to) {
      return undefined;
    }
    return to > from ? 'DOWN' : 'UP';
  },
  {
    memoizeOptions: {
      maxSize: 3000,
    },
  }
);

/**
 * @param {*}
 *  @property {Object} state Redux state
 *  @property {String} columnId Selected Column Id.
 * @returns {Boolean} `true` When one of the selected card has unread highlights. s
 * 											When no card is selected, returns `true` if column has unread highlights
 */
export const isUnreadChangesOnSelectedCards = ({ state, columnId }) => {
  const _selectedCards = board.selectors.selectedCards(state);
  const hasColumnUnread = isColumnUnread({ state, columnId });
  if (!hasColumnUnread || isEmpty(_selectedCards)) {
    return hasColumnUnread;
  }

  let hasUnreaChange = false;
  forEach(_selectedCards, (cardId) => {
    if (values(cardUnreadChanges({ state, cardId })).includes(true)) {
      hasUnreaChange = true;
      return;
    }
  });
  return hasUnreaChange;
};

/*
 * @returns {Boolean} `true` when any of the selected or all cards in VIEWS has unread highlights.
 */
export const isUnreadChangesOnViewsSelectedCards = ({ state, columnId }) => {
  const selectedCards = views.selectors.selectedCards(state);
  const _cardsList = views.selectors.cardsList({ state, columnId });

  const cardsByColumn = get(_cardsList, 'cards');
  let allCards = [];
  if (isEmpty(selectedCards)) {
    forEach(cardsByColumn, (obj) => {
      allCards.push(obj.cardId);
    });
  }

  const cards = !isEmpty(selectedCards) ? selectedCards : allCards;
  let hasUnreaChange = false;
  forEach(cards, (cardId) => {
    if (values(cardUnreadChanges({ state, cardId })).includes(true)) {
      hasUnreaChange = true;
      return false;
    }
  });
  return hasUnreaChange;
};

/**
 * @returns {Object} given column unread details.
 * @param {Object} state State
 * @param {String} columnId Column Id
 */
export const columnUnread = createSelector(
  ({ state, columnId }) => board.selectors.isBoardActive(state, columnSelectors.columnBoardId(state, columnId)),
  ({ state, columnId }) =>
    firestoreRedux.selectors.doc(
      state,
      'column-unread-user-details',
      `coluu_${getIdWoPrefix({ id: columnId, prefix: 'col_' })}_${getIdWoPrefix({
        id: authSelectors.currentUserId(state),
        prefix: 'usr_',
      })}`
    ),
  (isBoardActive, doc) => {
    if (!isBoardActive) {
      return {};
    }

    if(!doc || !doc.anyUnread) {
      return false;
    }
    
    return get(doc, 'unread', {});
  },
  {
    memoizeOptions: {
      maxSize: 50,
    },
  }
);

/**
 * @returns {Boolean} given column is unread or not.
 * @param {Object} state State
 * @param {String} columnId Column Id
 */
export const isColumnUnread = createSelector(
  ({ state, columnId }) => columnUnread({ state, columnId }),
  (unread) => {
    return get(unread, 'cards', false);
  },
  {
    memoizeOptions: {
      maxSize: 50,
    },
  }
);

/**
 * @param {Object} state Redux state
 * @param {String} boardId Board Id
 * @returns {Array} ids of the board's columns carrying any unread — created, renamed, re-ordered, or holding an unread card.
 */
export const unreadColumnIds = createSelector(
  (state, boardId) => board.selectors.columns(state, boardId),
  (state, boardId) => board.selectors.isBoardActive(state, boardId),
  (state) => authSelectors.currentUserId(state),
  (state) => firestoreRedux.selectors.collection(state, 'column-unread-user-details'),
  (columns, isBoardActive, userId, docs) => {
    if (!isBoardActive) {
      return [];
    }
    const userIdWoPrefix = getIdWoPrefix({ id: userId, prefix: 'usr_' });
    return columns
      .filter((column) => {
        const doc = column && get(docs, `coluu_${getIdWoPrefix({ id: column.id, prefix: 'col_' })}_${userIdWoPrefix}`);
        return doc && doc.anyUnread && values(doc.unread).some(Boolean);
      })
      .map((column) => column.id);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * @param {*}
 *  @property {Object} state Redux state
 *  @property {String} boardId Board Id
 * @returns {Object} unread details of board e.g. { chats: true, attachments: true }
 */
export const boardUnread = createSelector(
  ({ state, boardId }) => board.selectors.isBoardActive(state, boardId),
  ({ state, boardId }) =>
    firestoreRedux.selectors.doc(
      state,
      'board-unread-user-details',
      `buu_${getIdWoPrefix({ id: boardId, prefix: 'brd_' })}_${getIdWoPrefix({
        id: authSelectors.currentUserId(state),
        prefix: 'usr_',
      })}`
    ),
  (isBoardActive, doc) => {
    if (!isBoardActive) {
      return {};
    }
    
    if(!doc || !doc.anyUnread) {
      return false;
    }

    return get(doc, 'unread', {});
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * @returns {boolean} - True if the board has unread highlights, false otherwise.
 */
export const isBoardUnread = createSelector(
  (state, boardId) => boardUnread({state, boardId}),
  (unread) => {
    return unread && (unread.name || unread.columns || unread.attachments || unread.chats) || false;
  },
  {
    memoizeOptions: {
      maxSize: 50,
    },
  }
);

/**
 * @returns {boolean} - True if there are unread changes in hidden columns, false otherwise.
 */
export const hasUnreadChangesInHiddenColumns = ({state, boardId}) => {
  const _boardUnread = boardUnread({ state, boardId });
  if(!_boardUnread || !_boardUnread.columns) {
    return false;
  }
  
  const hiddenColumns = board.selectors.onlyHiddenColumns(state, boardId) || [];
  return hiddenColumns.some(column => {
    if(!column || !column.id) return false;
    
    const unread = columnUnread({ state, columnId: column.id });
    if(isEmpty(unread)) return false;
    
    return Object.values(unread).some(Boolean);
  });
}

/**
 * @param {String} attachmentId 
 * @returns {Object} attachment unread.
 */
export const _attachmentUnread = (state, attachmentId) => {
  return attachmentId && attachmentId.startsWith('ca_')
    ? firestoreRedux.selectors.doc(
        state,
        'card-attachment-unread-user-details',
        `cauu_${getIdWoPrefix({ id: attachmentId, prefix: 'ca_' })}_${getIdWoPrefix({
          id: authSelectors.currentUserId(state),
          prefix: 'usr_',
        })}`
      )
    : firestoreRedux.selectors.doc(
        state,
        'board-attachment-unread-user-details',
        `bauu_${getIdWoPrefix({ id: attachmentId, prefix: 'ba_' })}_${getIdWoPrefix({
          id: authSelectors.currentUserId(state),
          prefix: 'usr_',
        })}`
      );
};

/**
 * @param {Object} state Redux state.
 * @param {String} attachmentId Attachment ID passed to give attachment item model.
 * @return { Object } Card attachment unread model.
 *                    e.g. {created: true, title: true, content: true}
 */
export const cardAttachmentUnread = createSelector(
  (state, attachmentId) => board.selectors.isBoardActive(state, attachmentsSelectors.attachmentBoardId(state, attachmentId)),
  (state, attachmentId) => _attachmentUnread(state, attachmentId),
  (isBoardActive, doc) => {
    if (!isBoardActive) {
      return {};
    }

    if(!doc || !doc.anyUnread) {
      return {};
    }

    const unreads = get(doc, 'unread', {}) || {};
    if (unreads.created) {
      return { created: true };
    }
    return unreads;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 100,
    },
  }
);

/**
 * @param {Object} state Redux state.
 * @param {String} attachmentId Attachment ID passed to give attachment item model.
 * @return { Object } Board attachment unread model.
 *                    e.g. {created: true, title: true, content: true}
 */
export const boardAttachmentUnread = createSelector(
  (state, attachmentId) => board.selectors.isBoardActive(state, attachmentsSelectors.attachmentBoardId(state, attachmentId)),
  (state, attachmentId) => _attachmentUnread(state, attachmentId),
  (isBoardActive, doc) => {
    if (!isBoardActive) {
      return {};
    }

    if(!doc || !doc.anyUnread) {
      return {};
    }

    const unreads = get(doc, 'unread', {}) || {};
    if (unreads.created) {
      return { created: true };
    }
    return unreads;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 100,
    },
  }
);

/**
 * @param {Object} state Redux state.
 * @param {String} taskId task ID passed to give task item model.
 * @return { Object } task unread model.
 *                    e.g. {created, title, dueDate, closed}
 */
export const _taskUnread = (state, taskId) => firestoreRedux.selectors.doc(state, 'task-unread-user-details', `tuu_${getIdWoPrefix({id: taskId, prefix: 't_' })}_${getIdWoPrefix({id: authSelectors.currentUserId(state), prefix: 'usr_'})}`);
export const taskUnread = createSelector(
  (state, taskId) => board.selectors.isBoardActive(state, taskSelectors.taskBoardId(state, taskId)),
  (state, taskId) => _taskUnread(state, taskId),
  (isBoardActive, doc) => {
    if (!isBoardActive) {
      return {};
    }

    if(!doc || !doc.anyUnread) {
      return {};
    }

    const unreads = get(doc, 'unread', {}) || {};
    if (unreads.created) {
      return { created: true };
    }

    if(unreads.closed) {
      unreads.dueDate = false;
    }

    return unreads;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 100,
    },
  }
);

/**
 * @param {String} chatId 
 * @returns {Object} chat unread details.
 */
export const _chatUnred = (state, chatId) => {
  return chatId && chatId.startsWith('ccm_')
    ? firestoreRedux.selectors.doc(
        state,
        'card-chat-unread-user-details',
        `ccuu_${getIdWoPrefix({ id: chatId, prefix: 'ccm_' })}_${getIdWoPrefix({ id: authSelectors.currentUserId(state), prefix: 'usr_' })}`
      )
    : firestoreRedux.selectors.doc(
        state,
        'board-chat-unread-user-details',
        `bcuu_${getIdWoPrefix({ id: chatId, prefix: 'bcm_' })}_${getIdWoPrefix({ id: authSelectors.currentUserId(state), prefix: 'usr_' })}`
      );
};

/**
 * @param {Object} state Redux state.
 * @param {String} attachmentId chat ID passed to give chat item model.
 * @return { Object } Card chat unread model.
 *                    e.g. {created: true, message: true}
 */
export const cardChatUnread = createSelector(
  (state, chatId) => board.selectors.isBoardActive(state, chatSelectors.chatBoardId(state, chatId)),
  (state, chatId) => _chatUnred(state, chatId),
  (isBoardActive, doc) => {
    if (!isBoardActive) {
      return {};
    }

    if(!doc || !doc.anyUnread) {
      return {};
    }

    const unreads = get(doc, 'unread', {});
    if (unreads.created) {
      return { created: true };
    }
    return unreads;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 100,
    },
  }
);

/**
 * @param {Object} state Redux state.
 * @param {String} attachmentId chat ID passed to give chat item model.
 * @return { Object } Board chat unread model.
 *                    e.g. {created: true, message: true}
 */
export const boardChatUnread = createSelector(
  (state, chatId) => board.selectors.isBoardActive(state, chatSelectors.chatBoardId(state, chatId)),
  (state, chatId) => _chatUnred(state, chatId),
  (isBoardActive, doc) => {
    if (!isBoardActive) {
      return {};
    }

    if(!doc || !doc.anyUnread) {
      return {};
    }

    const unreads = get(doc, 'unread', {});
    if (unreads.created) {
      return { created: true };
    }
    return unreads;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 100,
    },
  }
);